import { usePersistedState } from "@/lib/persistedFilters";
// UC CDR → Statements. One statement per domain + currency for an explicit
// from/to window. Rating and building both run against the window chosen at the
// top of the page; every statement remembers the exact instants it covers.
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Calculator, Download, Loader2, Lock, LockOpen, RefreshCw, Search } from "lucide-react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Pill, STATUS_TONE, inZone, money } from "./ui";

export default function Statements({ window: win }) {
  const [batches, setBatches] = useState([]);
  const [batch, setBatch] = useState("");
  const [q, setQ] = usePersistedState("uccdr-statements.q", "");
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState([]);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);

  const windowKey = useMemo(
    () => (win?.from_at ? `${win.from_at}|${win.to_at}` : ""), [win?.from_at, win?.to_at]);

  const loadBatches = useCallback(async () => {
    const [{ data: b }, { data: r }] = await Promise.all([
      api.get("/uc-cdr/statements/batches"),
      api.get("/uc-cdr/rate-runs", { params: { limit: 5 } }),
    ]);
    setBatches(b.batches || []);
    setRuns(r.runs || []);
    return b.batches || [];
  }, []);

  // Follow the page window when it has statements, otherwise show the newest batch.
  useEffect(() => {
    loadBatches().then((rows) => {
      const match = rows.find((row) => row.range_key === windowKey);
      setBatch(match ? match.range_key : (rows[0]?.range_key || ""));
    });
  }, [loadBatches, windowKey]);

  const load = useCallback(async () => {
    if (!batch) return setData(null);
    const { data: d } = await api.get("/uc-cdr/statements", { params: { batch, q } });
    setData(d);
  }, [batch, q]);

  useEffect(() => { load(); }, [load]);

  const active = runs.some((r) => r.status === "running");
  useEffect(() => {
    if (!active) return undefined;
    const id = setInterval(() => { loadBatches(); load(); }, 4000);
    return () => clearInterval(id);
  }, [active, load, loadBatches]);

  const windowParams = { start: win?.from_at || "", end: win?.to_at || "", tz: win?.tz || "UTC" };
  const current = batches.find((b) => b.range_key === batch);
  const windowHasBatch = !!batches.find((b) => b.range_key === windowKey);

  const rate = async (force) => {
    if (!win?.from_at) return toast.error("Pick a date range at the top of the page first");
    setBusy("rate");
    try {
      await api.post("/uc-cdr/rate-run", null, { params: { ...windowParams, force } });
      toast.success(`Rating ${win.label}${force ? " from scratch" : ""} — statements follow`);
      await loadBatches();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start the rating run");
    } finally {
      setBusy("");
    }
  };

  const rebuild = async () => {
    setBusy("build");
    try {
      const { data: d } = await api.post("/uc-cdr/statements/build", null, { params: windowParams });
      toast.success(`${d.statements} statement(s) for ${d.label}`);
      const rows = await loadBatches();
      setBatch(rows.find((r) => r.range_key === windowKey)?.range_key || batch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not rebuild statements");
    } finally {
      setBusy("");
    }
  };

  const setStatus = async (stmt, status) => {
    setBusy(stmt.statement_id);
    try {
      await api.post(`/uc-cdr/statements/${stmt.statement_id}/status`, null, { params: { status } });
      toast.success(`${stmt.domain} ${status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change that statement");
    } finally {
      setBusy("");
    }
  };

  const view = async (stmt) => {
    const { data: d } = await api.get(`/uc-cdr/statements/${stmt.statement_id}`);
    setOpen(d);
  };

  return (
    <div className="space-y-4" data-testid="uc-cdr-statements">
      <div className="flex flex-wrap items-center gap-2">
        <select value={batch} onChange={(e) => setBatch(e.target.value)}
                data-testid="uc-cdr-statement-batch"
                className="h-9 max-w-[420px] rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700">
          {!batches.length && <option value="">nothing built yet</option>}
          {batches.map((b) => (
            <option key={b.range_key} value={b.range_key}>
              {`${b.label} — ${b.statements} statements`}
            </option>
          ))}
        </select>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input className="w-56 pl-8" placeholder="Domain or partner" value={q}
                 data-testid="uc-cdr-statement-search" onChange={(e) => setQ(e.target.value)} />
        </div>
        {(data?.totals || []).map((t) => (
          <Pill key={t.currency} tone="bg-emerald-100 text-emerald-800">
            {money(t.amount, t.currency)} · {t.minutes.toLocaleString()} min · {t.statements} statements
          </Pill>
        ))}
        <div className="ml-auto flex items-center gap-2">
          {active && <span className="text-xs text-amber-700" data-testid="uc-cdr-rating-active">rating…</span>}
          <Button size="sm" onClick={() => rate(false)} disabled={!!busy || active}
                  data-testid="uc-cdr-rate-run">
            {busy === "rate" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Rate this window
          </Button>
          <Button size="sm" variant="outline" onClick={() => rate(true)} disabled={!!busy || active}
                  data-testid="uc-cdr-rate-rerun">
            <RefreshCw className="h-4 w-4" /> Re-rate all
          </Button>
          <Button size="sm" variant="ghost" onClick={rebuild} disabled={!!busy}
                  data-testid="uc-cdr-statements-build">Rebuild</Button>
        </div>
      </div>

      {!windowHasBatch && !!win?.label && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-sky-200 bg-sky-50/60 px-4 py-2.5"
              data-testid="uc-cdr-window-no-batch">
          <span className="text-sm text-sky-900">
            No statements built for <strong>{win.label}</strong> yet
            {current ? ` — showing ${current.label} instead.` : "."}
          </span>
          <Button size="sm" variant="outline" onClick={() => rate(false)} disabled={!!busy || active}
                  data-testid="uc-cdr-window-rate">Rate it now</Button>
        </Card>
      )}

      {!!runs.length && (
        <Card className="px-4 py-2.5" data-testid="uc-cdr-rate-runs">
          <div className="flex flex-wrap items-center gap-3 text-[12px] text-slate-600">
            <span className="font-semibold text-slate-700">Last rating run</span>
            <Pill tone={runs[0].status === "ok" ? "bg-emerald-100 text-emerald-800"
              : runs[0].status === "running" ? "bg-amber-100 text-amber-800"
              : "bg-rose-100 text-rose-800"}>{runs[0].status}</Pill>
            <span>{runs[0].label || runs[0].period}</span>
            <span className="font-mono">{inZone(runs[0].started_at, runs[0].tz || "UTC")}</span>
            <span>{(runs[0].rated || 0).toLocaleString()} priced</span>
            <span>{(runs[0].zeroed || 0).toLocaleString()} zero-rated</span>
            <span className={runs[0].unmatched ? "text-amber-700" : ""}>
              {(runs[0].unmatched || 0).toLocaleString()} with no rate
            </span>
            {Object.entries(runs[0].amounts || {}).map(([cur, amt]) => (
              <span key={cur} className="font-semibold text-slate-800">{money(amt, cur)}</span>
            ))}
            {(runs[0].errors || [])[0] && <span className="text-rose-700">{runs[0].errors[0]}</span>}
          </div>
        </Card>
      )}

      <Card className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="text-[11px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5">Domain</th>
              <th className="px-3 py-1.5">Partner</th>
              <th className="px-3 py-1.5">Plan</th>
              <th className="px-3 py-1.5">From</th>
              <th className="px-3 py-1.5">Charged legs</th>
              <th className="px-3 py-1.5">Minutes</th>
              <th className="px-3 py-1.5">Amount</th>
              <th className="px-3 py-1.5">Status</th>
              <th className="px-3 py-1.5" />
            </tr>
          </thead>
          <tbody data-testid="uc-cdr-statements-body">
            {(data?.statements || []).map((s) => (
              <tr key={s.statement_id} className="border-t border-slate-100 hover:bg-slate-50/60">
                <td className="px-3 py-1.5 font-medium text-slate-800">
                  <button type="button" className="hover:underline" onClick={() => view(s)}
                          data-testid={`uc-cdr-statement-open-${s.domain}`}>{s.domain}</button>
                </td>
                <td className="px-3 py-1.5 text-slate-500">{s.territory || "—"}</td>
                <td className="px-3 py-1.5 text-slate-600">{s.plan}</td>
                <td className="px-3 py-1.5"><Pill>{s.source === "ns" ? "UC" : "SBC"}</Pill></td>
                <td className="px-3 py-1.5 text-slate-600">
                  {(s.charged_calls || 0).toLocaleString()} / {(s.calls || 0).toLocaleString()}
                  {!!s.unrated_calls && (
                    <span className="ml-1 text-amber-700">· {s.unrated_calls} no rate</span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-slate-600">{(s.minutes || 0).toLocaleString()}</td>
                <td className="px-3 py-1.5 font-semibold text-slate-800">{money(s.amount, s.currency)}</td>
                <td className="px-3 py-1.5"><Pill tone={STATUS_TONE[s.status]}>{s.status}</Pill></td>
                <td className="px-3 py-1.5 text-right">
                  <div className="flex justify-end gap-1">
                    <a href={`${API}/uc-cdr/statements/${s.statement_id}.csv?tz=${encodeURIComponent(s.tz || win?.tz || "UTC")}`}
                       data-testid={`uc-cdr-statement-csv-${s.domain}`}>
                      <Button size="sm" variant="ghost"><Download className="h-4 w-4" /></Button>
                    </a>
                    {s.status !== "synced" && (
                      <Button size="sm" variant="ghost" disabled={busy === s.statement_id}
                              onClick={() => setStatus(s, s.status === "closed" ? "open" : "closed")}
                              data-testid={`uc-cdr-statement-toggle-${s.domain}`}>
                        {s.status === "closed" ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {!data?.statements?.length && (
              <tr><td colSpan={9} className="px-3 py-8 text-center text-sm text-slate-500"
                      data-testid="uc-cdr-statements-empty">
                Nothing built yet — set a range above and hit "Rate this window".
              </td></tr>
            )}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-3xl" data-testid="uc-cdr-statement-detail">
          <DialogHeader>
            <DialogTitle>
              {open?.domain} · {money(open?.amount, open?.currency)}
            </DialogTitle>
            <DialogDescription>
              {open?.label} · {open?.plan} plan · rated from{" "}
              {open?.source === "ns" ? "the UC Platform" : "the SBCs"}
            </DialogDescription>
          </DialogHeader>
          {open && (
            <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
              <div className="flex flex-wrap gap-2 text-[12px] text-slate-600">
                <Pill tone={STATUS_TONE[open.status]}>{open.status}</Pill>
                <span>{open.charged_calls} charged of {open.calls} legs</span>
                <span>{open.minutes} min</span>
                <span className="font-mono">{open.from_at} → {open.to_at}</span>
                {!!open.unrated_calls && (
                  <span className="text-amber-700">{open.unrated_calls} legs with no rate</span>
                )}
              </div>

              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  By destination
                </div>
                <table className="w-full text-left text-sm">
                  <tbody>
                    {(open.by_region || []).map((r) => (
                      <tr key={r.region} className="border-t border-slate-100">
                        <td className="py-1 pr-3 text-slate-700">{r.region}</td>
                        <td className="py-1 pr-3 text-slate-500">{r.calls} calls</td>
                        <td className="py-1 pr-3 text-slate-500">{r.minutes} min</td>
                        <td className="py-1 font-medium text-slate-800">{money(r.amount, open.currency)}</td>
                      </tr>
                    ))}
                    {!open.by_region?.length && (
                      <tr><td className="py-2 text-sm text-slate-500">
                        Nothing charged — every leg was domestic or inbound.
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {!!open.unrated_numbers?.length && (
                <div>
                  <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-700">
                    Numbers with no matching rate
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {open.unrated_numbers.map((n) => (
                      <Pill key={n.number} tone="bg-amber-100 text-amber-800">
                        {n.number} · {n.calls}
                      </Pill>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Priciest legs ({open.tz})
                </div>
                <table className="w-full text-left text-sm">
                  <tbody>
                    {(open.top_calls || []).map((c, i) => (
                      <tr key={`${c.started_at}-${i}`} className="border-t border-slate-100">
                        <td className="py-1 pr-3 font-mono text-[12px] text-slate-600"
                            title={`${c.started_at} UTC`}>{inZone(c.started_at, open.tz)}</td>
                        <td className="py-1 pr-3 font-mono text-[12px] text-slate-700">{c.rated_digits}</td>
                        <td className="py-1 pr-3 text-slate-600">{c.rate_region}</td>
                        <td className="py-1 pr-3 text-slate-500">{c.billed_seconds}s @ {c.rate}</td>
                        <td className="py-1 font-medium text-slate-800">{money(c.amount, open.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
